import { useEffect, useState } from "react";
import { toast } from "sonner";
import { MessageSquareDashed, ImagePlus, Loader2, X, RotateCcw, CalendarClock, MousePointerClick, FlaskConical, Timer } from "lucide-react";
import { api, errText } from "../../lib/api";
import { compressImage } from "../../lib/image";
import { PopupCard } from "../../components/HomePopup";
import { PopupShell } from "../../components/PopupShell";
import { PopupAbTest } from "./PopupAbTest";
import { PopupStats } from "./PopupStats";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { Switch } from "../../components/ui/switch";
import { Checkbox } from "../../components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Slider } from "../../components/ui/slider";

const FREQ = [
  ["once", "Once per visitor (until you change the popup)"],
  ["daily", "Once per day"],
  ["session", "Once per browser session"],
  ["every", "Every time they open this page"],
];

const Field = ({ label, k, form, set, area, max, pre = "popup" }) => (
  <div>
    <Label className="text-xs text-slate-500">{label}</Label>
    {area
      ? <Textarea value={form[k]} maxLength={max} onChange={(e) => set(k, e.target.value)} rows={3} className="mt-1 rounded-xl" data-testid={`${pre}-${k}`} />
      : <Input value={form[k]} maxLength={max} onChange={(e) => set(k, e.target.value)} className="mt-1 rounded-xl" data-testid={`${pre}-${k}`} />}
  </div>
);

const COLOR_OPTS = [["orange", "Orange", "bg-[#F5551E]"], ["navy", "Navy", "bg-[#12234A]"], ["white", "White", "bg-white border border-slate-300"]];
const ColorPick = ({ label, k, def, vals, setV, pre = "popup" }) => (
  <div>
    <Label className="text-xs text-slate-500">{label}</Label>
    <div className="mt-1 flex gap-2" data-testid={`${pre}-${k}`}>
      {COLOR_OPTS.map(([v, l, sw]) => {
        const on = (vals[k] || def) === v;
        return (
          <button key={v} type="button" onClick={() => setV(k, v)} data-testid={`${pre}-${k}-${v}`}
            className={`inline-flex items-center gap-2 h-9 px-3 rounded-xl border text-sm transition-colors ${on ? "border-[#F5551E] bg-orange-50 font-semibold text-[#12234A]" : "border-slate-200 text-slate-600 hover:border-slate-300"}`}>
            <span className={`h-4 w-4 rounded-full ${sw}`} /> {l}
          </button>
        );
      })}
    </div>
  </div>
);

const isoToLocal = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  return new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().slice(0, 16);
};
const localToIso = (v) => (v ? new Date(v).toISOString() : "");
const fromServer = (d) => ({ ...d, width_px: d.width_px || 512, image_height: d.image_height || 320, position: d.position || "center", button_position: d.button_position || "below-left", start_at: isoToLocal(d.start_at), end_at: isoToLocal(d.end_at) });

const Toggle = ({ id, checked, onChange, label, hint }) => (
  <label className="flex items-start gap-3 cursor-pointer">
    <Switch checked={checked} onCheckedChange={onChange} data-testid={id} className="mt-0.5" />
    <span><span className="block text-sm font-semibold text-[#12234A]">{label}</span>{hint && <span className="block text-xs text-slate-500">{hint}</span>}</span>
  </label>
);

const Tick = ({ id, checked, onChange, children }) => (
  <label className="flex items-center gap-2.5 text-sm text-[#12234A] cursor-pointer flex-wrap">
    <Checkbox checked={checked} onCheckedChange={(v) => onChange(!!v)} data-testid={id} className="data-[state=checked]:bg-[#F5551E] data-[state=checked]:border-[#F5551E]" />
    {children}
  </label>
);

const num = (v, lo, hi) => Math.max(lo, Math.min(hi, Number(v) || 0));

const SizeSlider = ({ id, label, value, min, max, step, onChange, hint }) => (
  <div>
    <div className="flex items-center justify-between text-sm">
      <span className="font-semibold text-[#12234A]">{label}</span>
      <span className="text-slate-500 tabular-nums" data-testid={`${id}-value`}>{value}px</span>
    </div>
    <Slider value={[value]} min={min} max={max} step={step} onValueChange={([v]) => onChange(v)} className="mt-2" data-testid={id} />
    {hint && <p className="text-[11px] text-slate-400 mt-1">{hint}</p>}
  </div>
);

const POSITIONS = [
  ["center", "Centred", "Dims the page"],
  ["bottom-right", "Bottom right", "Slide-in"],
  ["bottom-left", "Bottom left", "Slide-in"],
  ["top-right", "Top right", "Slide-in"],
  ["top-left", "Top left", "Slide-in"],
  ["bottom-bar", "Bottom bar", "Full width"],
];
const DOT = { center: "inset-0 m-auto h-3 w-5", "bottom-right": "bottom-1 right-1 h-3 w-4", "bottom-left": "bottom-1 left-1 h-3 w-4",
  "top-right": "top-1 right-1 h-3 w-4", "top-left": "top-1 left-1 h-3 w-4", "bottom-bar": "bottom-0 inset-x-0 h-2" };

const PositionPicker = ({ form, set }) => (
  <div>
    <p className="text-sm font-semibold text-[#12234A] mb-2">Position</p>
    <div className="grid grid-cols-3 sm:grid-cols-6 gap-2" data-testid="popup-position">
      {POSITIONS.map(([v, l, hint]) => (
        <button key={v} type="button" onClick={() => set("position", v)} data-testid={`popup-position-${v}`}
          className={`rounded-xl border p-2 text-left transition-colors ${form.position === v ? "border-[#F5551E] bg-orange-50" : "border-slate-200 hover:border-slate-300"}`}>
          <span className={`relative block h-9 rounded-md mb-1.5 ${v === "center" ? "bg-slate-300" : "bg-slate-100"}`}>
            <span className={`absolute rounded-sm ${form.position === v ? "bg-[#F5551E]" : "bg-slate-400"} ${DOT[v]}`} />
          </span>
          <span className="block text-xs font-semibold text-[#12234A] leading-tight">{l}</span>
          <span className="block text-[10px] text-slate-400">{hint}</span>
        </button>
      ))}
    </div>
    <p className="text-[11px] text-slate-400 mt-1.5">Corner and bottom-bar popups don't dim the page — visitors can keep scrolling and clicking.</p>
  </div>
);

const BTN_SPOTS = [
  ["On the picture", [["image-top-left", "Top left"], ["image-top-center", "Top centre"], ["image-top-right", "Top right"],
    ["image-bottom-left", "Bottom left"], ["image-bottom-center", "Bottom centre"], ["image-bottom-right", "Bottom right"]]],
  ["Below the text", [["below-left", "Left"], ["below-center", "Centre"], ["below-right", "Right"], ["below-full", "Full width"]]],
];
const spotDot = (v) => {
  const [w, a, b] = v.split("-");
  const h = w === "image" ? b : a;
  const x = { left: "left-1", center: "left-1/2 -translate-x-1/2", right: "right-1", full: "inset-x-1" }[h];
  const y = w === "image" ? (a === "top" ? "top-1" : "top-[18px]") : "bottom-1";
  return `${x} ${y} ${h === "full" ? "" : "w-3"}`;
};

const ButtonPositionPicker = ({ form, set }) => {
  const cur = form.button_position || "below-left";
  return (
    <div data-testid="popup-button-position">
      <p className="text-sm font-semibold text-[#12234A]">Button position</p>
      {BTN_SPOTS.map(([group, spots]) => (
        <div key={group} className="mt-2">
          <p className="text-[11px] font-semibold uppercase tracking-wider text-slate-400 mb-1.5">{group}</p>
          <div className="grid grid-cols-3 sm:grid-cols-6 gap-2">
            {spots.map(([v, l]) => (
              <button key={v} type="button" onClick={() => set("button_position", v)} data-testid={`popup-button-position-${v}`}
                className={`rounded-xl border p-2 text-left transition-colors ${cur === v ? "border-[#F5551E] bg-orange-50" : "border-slate-200 hover:border-slate-300"}`}>
                <span className="relative block h-9 rounded-md mb-1.5 bg-slate-100 overflow-hidden">
                  <span className="absolute inset-x-0 top-0 h-6 bg-slate-300" />
                  <span className={`absolute h-1.5 rounded-full ${cur === v ? "bg-[#F5551E]" : "bg-slate-500"} ${spotDot(v)}`} />
                </span>
                <span className="block text-xs font-semibold text-[#12234A] leading-tight">{l}</span>
              </button>
            ))}
          </div>
        </div>
      ))}
      <p className="text-[11px] text-slate-400 mt-1.5">"On the picture" needs an image; without one, the button sits below the text on the same side.</p>
    </div>
  );
};

const SizeControls = ({ form, set }) => (
  <div className="rounded-xl border border-slate-200 p-4 space-y-4" data-testid="popup-size">
    <div className="flex items-center justify-between">
      <p className="text-sm font-semibold text-[#12234A]">Position & size</p>
      <button type="button" onClick={() => { set("width_px", 512); set("image_height", 320); }} className="text-xs font-semibold text-slate-500 hover:text-[#F5551E]" data-testid="popup-size-reset">Default size</button>
    </div>
    <PositionPicker form={form} set={set} />
    {form.position === "bottom-bar"
      ? <p className="text-xs text-slate-500" data-testid="popup-bar-note">The bottom bar always spans the full width, with a small image on the left.</p>
      : (
        <>
          <SizeSlider id="popup-width" label="Width" value={form.width_px} min={320} max={900} step={10} onChange={(v) => set("width_px", v)}
            hint="On phones it always fits the screen. Default 512px." />
          <SizeSlider id="popup-image-height" label="Image height (max)" value={form.image_height} min={120} max={600} step={10} onChange={(v) => set("image_height", v)}
            hint="Taller images are cropped to this height. Default 320px." />
          <ButtonPositionPicker form={form} set={set} />
        </>
      )}
  </div>
);

const OpenTriggers = ({ form, set }) => {
  const none = !form.trigger_delay && !form.trigger_scroll && !form.trigger_exit;
  return (
    <div className="rounded-xl border border-slate-200 p-4 space-y-3" data-testid="popup-triggers">
      <p className="text-sm font-semibold text-[#12234A] flex items-center gap-1.5"><MousePointerClick size={15} className="text-[#F5551E]" /> When it opens <span className="font-normal text-xs text-slate-500">— whichever happens first</span></p>
      <Tick id="popup-trigger-delay" checked={form.trigger_delay} onChange={(v) => set("trigger_delay", v)}>
        After
        <Input type="number" min={0} max={30} value={form.delay_seconds} onChange={(e) => set("delay_seconds", num(e.target.value, 0, 30))} className="h-8 w-16 rounded-lg" data-testid="popup-delay" />
        seconds
      </Tick>
      <Tick id="popup-trigger-scroll" checked={form.trigger_scroll} onChange={(v) => set("trigger_scroll", v)}>
        When they scroll
        <Input type="number" min={10} max={90} step={5} value={form.scroll_percent} onChange={(e) => set("scroll_percent", num(e.target.value, 10, 90))} className="h-8 w-16 rounded-lg" data-testid="popup-scroll-percent" />
        % down the page
      </Tick>
      <Tick id="popup-trigger-exit" checked={form.trigger_exit} onChange={(v) => set("trigger_exit", v)}>When they're about to leave (mouse heads for the tab bar)</Tick>
      {form.trigger_exit && <p className="text-xs text-slate-500 pl-7" data-testid="popup-exit-phone-note">On phones and tablets this uses the {form.delay_seconds}-second delay instead.</p>}
      {none && <p className="text-xs text-red-600" data-testid="popup-triggers-error">Tick at least one.</p>}
    </div>
  );
};

const AutoCloseSetting = ({ form, set }) => {
  const on = (form.auto_close_seconds || 0) > 0;
  return (
    <div className="rounded-xl border border-slate-200 p-4 space-y-3" data-testid="popup-auto-close">
      <Toggle id="popup-auto-close-enabled" checked={on} onChange={(v) => set("auto_close_seconds", v ? 10 : 0)}
        label="Close automatically" hint="A thin bar shows the time left. It pauses while the visitor points at or types in the popup." />
      {on && (
        <div className="flex items-center gap-2 text-sm text-[#12234A] pl-1">
          <Timer size={15} className="text-[#F5551E]" /> Close after
          <Input type="number" min={3} max={120} value={form.auto_close_seconds} onChange={(e) => set("auto_close_seconds", num(e.target.value, 3, 120))} className="h-8 w-20 rounded-lg" data-testid="popup-auto-close-seconds" />
          seconds
        </div>
      )}
    </div>
  );
};

const ScheduleAudience = ({ form, set }) => (
  <div className="rounded-xl border border-slate-200 p-4 space-y-3" data-testid="popup-schedule">
    <p className="text-sm font-semibold text-[#12234A] flex items-center gap-1.5"><CalendarClock size={15} className="text-[#F5551E]" /> Schedule & audience</p>
    <div className="grid sm:grid-cols-2 gap-3">
      <div>
        <Label className="text-xs text-slate-500">Starts (optional)</Label>
        <Input type="datetime-local" value={form.start_at} onChange={(e) => set("start_at", e.target.value)} className="mt-1 rounded-xl" data-testid="popup-start" />
      </div>
      <div>
        <Label className="text-xs text-slate-500">Ends (optional)</Label>
        <Input type="datetime-local" value={form.end_at} onChange={(e) => set("end_at", e.target.value)} className="mt-1 rounded-xl" data-testid="popup-end" />
      </div>
    </div>
    <p className="text-xs text-slate-500">Leave blank to show it as soon as it's on and keep it showing. The popup must also be switched on.</p>
    <Toggle id="popup-signed-out-only" checked={form.signed_out_only} onChange={(v) => set("signed_out_only", v)}
      label="Only show to visitors who aren't signed in" hint="Members who are logged in won't see it" />
  </div>
);

const SignupSettings = ({ form, set, pre, page }) => page === "listing" ? (
  <div className="rounded-xl border border-slate-200 p-4 space-y-3" data-testid={`${pre}-signup-settings`}>
    <Toggle id={`${pre}-signup-enabled`} checked={form.signup_enabled} onChange={(v) => set("signup_enabled", v)}
      label="Collect “more like this” alerts" hint="Visitors type their email and pick how far away. They get one email a day with new listings of the same breed (or category when no breed is set). See them under Admin → Subscribers." />
    {form.signup_enabled && (
      <>
        <div className="pl-1">
          <Toggle id={`${pre}-signup-offer-newsletter`} checked={form.signup_newsletter} onChange={(v) => set("signup_newsletter", v)} label="Also offer the newsletter (unticked by default)" />
        </div>
        <div className="grid sm:grid-cols-2 gap-3">
          <Field label="Sign-up button text" k="signup_button_text" form={form} set={set} max={40} pre={pre} />
          <Field label="Sign-up button text (French)" k="signup_button_text_fr" form={form} set={set} max={40} pre={pre} />
          <Field label="Thank-you message" k="signup_thanks" form={form} set={set} max={300} pre={pre} />
          <Field label="Thank-you message (French)" k="signup_thanks_fr" form={form} set={set} max={300} pre={pre} />
        </div>
      </>
    )}
  </div>
) : (
  <div className="rounded-xl border border-slate-200 p-4 space-y-3" data-testid={`${pre}-signup-settings`}>
    <Toggle id={`${pre}-signup-enabled`} checked={form.signup_enabled} onChange={(v) => set("signup_enabled", v)}
      label="Collect emails in the popup" hint="Visitors type their email and choose what they want. View and email them under Admin → Subscribers." />
    {form.signup_enabled && (
      <>
        <div className="flex flex-wrap gap-5 pl-1">
          <Toggle id={`${pre}-signup-offer-newsletter`} checked={form.signup_newsletter} onChange={(v) => set("signup_newsletter", v)} label="Offer the newsletter" />
          <Toggle id={`${pre}-signup-offer-alerts`} checked={form.signup_alerts} onChange={(v) => set("signup_alerts", v)} label="Offer lost-pet alerts near them" />
        </div>
        <div className="grid sm:grid-cols-2 gap-3">
          <Field label="Sign-up button text" k="signup_button_text" form={form} set={set} max={40} pre={pre} />
          <Field label="Sign-up button text (French)" k="signup_button_text_fr" form={form} set={set} max={40} pre={pre} />
          <Field label="Thank-you message" k="signup_thanks" form={form} set={set} max={300} pre={pre} />
          <Field label="Thank-you message (French)" k="signup_thanks_fr" form={form} set={set} max={300} pre={pre} />
        </div>
      </>
    )}
  </div>
);

const ContentFields = ({ vals, setV, pre, page }) => (
  <div className="space-y-4" data-testid={`${pre}-content`}>
    {page === "listing" && (
      <p className="text-xs text-slate-600 bg-orange-50 rounded-lg px-3 py-2" data-testid="popup-placeholder-hint">
        Use <b>{"{breed}"}</b> and <b>{"{city}"}</b> in any text — they become the listing's breed (or category) and city, e.g. “Get alerts for more {"{breed}"} puppies”. Shows on active pet listings only, never to the seller or staff.
      </p>
    )}
    <div className="grid sm:grid-cols-2 gap-3">
      <Field label="Title" k="title" form={vals} set={setV} max={120} pre={pre} />
      <Field label="Title (French)" k="title_fr" form={vals} set={setV} max={120} pre={pre} />
      <Field label="Message" k="body" form={vals} set={setV} area max={1500} pre={pre} />
      <Field label="Message (French)" k="body_fr" form={vals} set={setV} area max={1500} pre={pre} />
      <Field label="Button text (optional)" k="button_text" form={vals} set={setV} max={40} pre={pre} />
      <Field label="Button text (French)" k="button_text_fr" form={vals} set={setV} max={40} pre={pre} />
    </div>
    <Field label="Button link — a page like /post or /browse, or a full https:// address" k="button_link" form={vals} set={setV} max={500} pre={pre} />
    <ColorPick label="Button colour" k="button_color" def="orange" vals={vals} setV={setV} pre={pre} />
    <div className="rounded-xl border border-dashed border-slate-200 p-4 space-y-3" data-testid="popup-second-button">
      <p className="text-sm font-semibold text-[#12234A]">Second button (optional) <span className="font-normal text-slate-400">— sits beside the first, e.g. "Browse Pets"</span></p>
      <div className="grid sm:grid-cols-2 gap-3">
        <Field label="Second button text" k="button2_text" form={vals} set={setV} max={40} pre={pre} />
        <Field label="Second button text (French)" k="button2_text_fr" form={vals} set={setV} max={40} pre={pre} />
      </div>
      <Field label="Second button link" k="button2_link" form={vals} set={setV} max={500} pre={pre} />
      <ColorPick label="Second button colour" k="button2_color" def="white" vals={vals} setV={setV} pre={pre} />
    </div>
    <div>
      <Label className="text-xs text-slate-500">Image (optional)</Label>
      <div className="mt-1"><ImagePicker value={vals.image} onChange={(v) => setV("image", v)} pre={pre} /></div>
    </div>
    <SignupSettings form={vals} set={setV} pre={pre} page={page} />
  </div>
);

const ImagePicker = ({ value, onChange, pre }) => {
  const [busy, setBusy] = useState(false);
  const onFile = async (e) => {
    const file = (e.target.files || [])[0];
    e.target.value = "";
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", await compressImage(file));
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      onChange(data.path);
    } catch (err) { toast.error(errText(err)); }
    finally { setBusy(false); }
  };
  return (
    <div className="flex items-center gap-3">
      <label data-testid={`${pre}-image-upload`} className="flex items-center gap-1.5 text-sm font-semibold text-[#F5551E] cursor-pointer hover:underline">
        {busy ? <Loader2 size={15} className="animate-spin" /> : <ImagePlus size={15} />} {value ? "Replace image" : "Upload image"}
        <input type="file" accept="image/*" className="hidden" onChange={onFile} />
      </label>
      {value && <button onClick={() => onChange("")} data-testid={`${pre}-image-remove`} className="flex items-center gap-1 text-sm text-slate-500 hover:text-red-600"><X size={14} /> Remove</button>}
    </div>
  );
};

const PAGES = [
  ["home", "Homepage", "the homepage"],
  ["browse", "Browse", "the Browse page (all categories except Lost & Found)"],
  ["lostfound", "Lost & Found", "Lost & Found browsing and the Lost & Found map"],
  ["listing", "Listing pages", "individual pet listing pages"],
];
const SAMPLE = { breed: "Golden Retriever", city: "Ottawa" };
const fillSample = (p) => Object.fromEntries(Object.entries(p).map(([k, v]) => [k, typeof v === "string" ? v.replaceAll("{breed}", SAMPLE.breed).replaceAll("{city}", SAMPLE.city) : v]));

export function PopupSettings() {
  const [page, setPage] = useState("home");
  const [dirty, setDirty] = useState(false);
  const switchTo = (v) => {
    if (v === page) return;
    if (dirty && !window.confirm("You have unsaved changes on this popup. Switch pages and lose them?")) return;
    setDirty(false);
    setPage(v);
  };
  return (
    <div className="bg-white rounded-2xl border border-slate-200/80 p-6 mt-6" data-testid="popup-settings">
      <div className="flex items-center gap-2 mb-1">
        <MessageSquareDashed size={17} className="text-[#F5551E]" />
        <h3 className="font-display font-bold text-[#12234A]">Popups</h3>
      </div>
      <p className="text-xs text-slate-500 mb-4">Each page has its own popup. A visitor sees at most one popup per visit, whichever page shows one first.</p>
      <div className="flex rounded-xl border border-slate-200 overflow-x-auto max-w-full w-fit text-sm mb-5 whitespace-nowrap" data-testid="popup-page-tabs">
        {PAGES.map(([v, l]) => (
          <button key={v} onClick={() => switchTo(v)} data-testid={`popup-page-${v}`}
            className={`px-4 py-1.5 font-semibold transition-colors ${page === v ? "bg-[#F5551E] text-white" : "text-slate-500 hover:bg-slate-50"}`}>{l}</button>
        ))}
      </div>
      <PagePopupSettings key={page} page={page} onDirty={setDirty} />
    </div>
  );
}

function PagePopupSettings({ page, onDirty }) {
  const [form, setForm] = useState(null);
  const [saved, setSaved] = useState(null);
  const [saving, setSaving] = useState(false);
  const [lang, setLang] = useState("en");
  const [editing, setEditing] = useState("A");
  const [testing, setTesting] = useState(false);
  const [, label, where] = PAGES.find((x) => x[0] === page);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const setB = (k, v) => setForm((f) => ({ ...f, variant_b: { ...f.variant_b, [k]: v } }));
  const load = (data) => { const f = fromServer(data); setForm(f); setSaved(f); };
  useEffect(() => { api.get("/admin/site/popup", { params: { page } }).then((r) => load(r.data)).catch((e) => toast.error(errText(e))); }, [page]);
  useEffect(() => { onDirty(!!form && JSON.stringify(form) !== JSON.stringify(saved)); }, [form, saved, onDirty]);

  const save = async (showAgain = false, abReset = false) => {
    setSaving(true);
    try {
      const { data } = await api.put("/admin/site/popup", { ...form, start_at: localToIso(form.start_at), end_at: localToIso(form.end_at), show_again: showAgain, ab_reset: abReset }, { params: { page } });
      load(data);
      toast.success(abReset ? "Saved — A/B test restarted from zero" : showAgain ? "Saved — everyone will see the popup again" : data.live_now ? `${label} popup saved and live` : data.enabled ? "Popup saved — it will show during its scheduled dates" : "Popup saved (switched off)");
    } catch (e) { toast.error(errText(e)); }
    finally { setSaving(false); }
  };
  const winner = async (v) => {
    setSaving(true);
    try {
      const { data } = await api.post("/admin/site/popup/ab/winner", { variant: v }, { params: { page } });
      load(data);
      setEditing("A");
      toast.success(`Version ${v} is now shown to everyone`);
    } catch (e) { toast.error(errText(e)); }
    finally { setSaving(false); }
  };
  if (!form) return null;
  const isB = form.ab_enabled && editing === "B";
  const shown = { ...(page === "listing" ? fillSample(isB ? { ...form, ...form.variant_b } : form) : isB ? { ...form, ...form.variant_b } : form), _page: page };

  return (
    <div data-testid={`popup-editor-${page}`}>
      {testing && <PopupShell p={shown} open onClose={() => setTesting(false)} lang={lang} page={page} test />}
      <PopupStats page={page} signup={form.signup_enabled} />
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <h4 className="font-display font-bold text-[#12234A]" data-testid="popup-editor-title">{label} popup</h4>
        <label className="flex items-center gap-2 text-sm font-semibold text-[#12234A]">
          {form.enabled && (
            <span data-testid="popup-live-status" className={`mr-1 px-2 py-0.5 rounded-full text-[11px] font-bold ${form.live_now ? "bg-emerald-50 text-emerald-700" : "bg-amber-50 text-amber-700"}`}>
              {form.live_now ? "Showing now" : form.title || form.body || form.image || form.signup_enabled ? "Not showing now (outside its dates)" : "Not showing (no content)"}
            </span>
          )}
          <Switch checked={form.enabled} onCheckedChange={(v) => set("enabled", v)} data-testid="popup-enabled" />
          {form.enabled ? "On" : "Off"}
        </label>
      </div>
      <p className="text-xs text-slate-500 mt-1 mb-5">A window that opens on {where}. French visitors see the French wording when it's filled in.</p>
      <div className="grid lg:grid-cols-[1fr_380px] gap-6">
        <div className="space-y-4">
          <PopupAbTest form={form} set={set} editing={editing} setEditing={setEditing} onReset={() => save(false, true)} onWinner={winner} saving={saving} />
          {isB
            ? <ContentFields key="B" vals={form.variant_b} setV={setB} pre="popup-b" page={page} />
            : <ContentFields key="A" vals={form} setV={set} pre="popup" page={page} />}
          <div>
            <Label className="text-xs text-slate-500">How often</Label>
            <Select value={form.frequency} onValueChange={(v) => set("frequency", v)}>
              <SelectTrigger className="mt-1 rounded-xl" data-testid="popup-frequency"><SelectValue /></SelectTrigger>
              <SelectContent>{FREQ.map(([v, l]) => <SelectItem key={v} value={v} data-testid={`popup-frequency-${v}`}>{l}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <OpenTriggers form={form} set={set} />
          <AutoCloseSetting form={form} set={set} />
          <SizeControls form={form} set={set} />
          <ScheduleAudience form={form} set={set} />
          <div className="flex gap-2 flex-wrap pt-1">
            <Button onClick={() => save(false)} disabled={saving} className="rounded-xl bg-[#F5551E] hover:bg-[#e04812]" data-testid="popup-save">{saving ? <Loader2 size={15} className="animate-spin" /> : "Save popup"}</Button>
            <Button variant="outline" onClick={() => setTesting(true)} disabled={!(shown.title || shown.body || shown.image || shown.signup_enabled)} className="rounded-xl gap-1.5" data-testid="popup-test-btn"><FlaskConical size={14} /> Test popup{form.ab_enabled ? ` (version ${isB ? "B" : "A"})` : ""}</Button>
            {form.frequency !== "every" && (
              <Button variant="outline" onClick={() => save(true)} disabled={saving} className="rounded-xl gap-1.5" data-testid="popup-show-again"><RotateCcw size={14} /> Save & show to everyone again</Button>
            )}
          </div>
        </div>
        <div>
          <div className="flex items-center justify-between mb-2">
            <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide" data-testid="popup-preview-label">Preview{form.ab_enabled ? ` · version ${isB ? "B" : "A"}` : ""}{form.width_px > 380 ? ` · ${Math.round((380 / form.width_px) * 100)}% scale` : ""}</p>
            <div className="flex rounded-lg border border-slate-200 overflow-hidden text-xs">
              {["en", "fr"].map((l) => (
                <button key={l} onClick={() => setLang(l)} data-testid={`popup-preview-${l}`} className={`px-2.5 py-1 font-semibold transition-colors ${lang === l ? "bg-[#12234A] text-white" : "text-slate-500"}`}>{l.toUpperCase()}</button>
              ))}
            </div>
          </div>
          <div className="rounded-2xl overflow-hidden shadow-xl border border-slate-100 bg-white pointer-events-none mx-auto" style={{ width: `${form.width_px}px`, zoom: Math.min(1, 380 / form.width_px) }} data-testid="popup-preview">
            {shown.title || shown.body || shown.image || shown.signup_enabled
              ? <PopupCard key={isB ? "B" : "A"} p={shown} lang={lang} onClose={() => {}} preview />
              : <p className="p-8 text-sm text-slate-400 text-center">Add a title, message or image to preview</p>}
          </div>
        </div>
      </div>
    </div>
  );
}
